
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DeviceRegistration from '@/components/settings/DeviceRegistration';
import AccountSettings from '@/components/settings/AccountSettings';
import NotificationPreferences from '@/components/settings/NotificationPreferences';
import SystemConfiguration from '@/components/settings/SystemConfiguration';
import UserManagement from '@/components/settings/user-management/UserManagement';
import DataSourcesSettings from '@/components/settings/data-sources/DataSourcesSettings';
import DeviceGroupsSettings from '@/components/settings/device-groups';
import ProtocolHandlersSettings from '@/components/settings/ProtocolHandlersSettings';
import SecurityAccessSettings from '@/components/settings/SecurityAccessSettings';
import AIIntegrationSettings from '@/components/settings/ai-integration';
import TariffBillingSettings from '@/components/settings/TariffBillingSettings';
import SustainabilitySettings from '@/components/settings/sustainability';
import WebhooksApiSettings from '@/components/settings/webhooks-api';

interface SettingsContentProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  editing: Record<string, boolean>;
  handleEditToggle: (section: string) => void;
  handleSave: (section: string) => void;
}

const SettingsContent = ({ 
  activeTab, 
  setActiveTab, 
  editing, 
  handleEditToggle, 
  handleSave 
}: SettingsContentProps) => {
  return (
    <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="hidden">
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="devicegroups">Device Groups</TabsTrigger>
          <TabsTrigger value="protocols">Protocol Handlers</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="datasources">Data Sources</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
          <TabsTrigger value="security">Security & Access</TabsTrigger>
          <TabsTrigger value="ai">AI & LLM</TabsTrigger>
          <TabsTrigger value="billing">Tariff & Billing</TabsTrigger>
          <TabsTrigger value="sustainability">Sustainability</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks & APIs</TabsTrigger>
        </TabsList>
        
        {/* System Configuration */}
        <TabsContent value="devices" className="space-y-6 mt-0">
          <DeviceRegistration />
        </TabsContent>
        
        <TabsContent value="devicegroups" className="mt-0">
          <DeviceGroupsSettings 
            editing={editing.devicegroups}
            onEditToggle={() => handleEditToggle('devicegroups')}
            onSave={() => handleSave('devicegroups')}
          />
        </TabsContent>
        
        <TabsContent value="protocols" className="mt-0">
          <ProtocolHandlersSettings 
            editing={editing.protocols}
            onEditToggle={() => handleEditToggle('protocols')}
            onSave={() => handleSave('protocols')}
          />
        </TabsContent>
        
        {/* Organization */}
        <TabsContent value="account" className="mt-0">
          <AccountSettings 
            editing={editing.account}
            onEditToggle={() => handleEditToggle('account')}
            onSave={() => handleSave('account')}
          />
        </TabsContent>
        
        <TabsContent value="datasources" className="mt-0">
          <DataSourcesSettings 
            editing={editing.datasources}
            onEditToggle={() => handleEditToggle('datasources')}
            onSave={() => handleSave('datasources')}
          />
        </TabsContent>
        
        <TabsContent value="users" className="mt-0">
          <UserManagement 
            editing={editing.users}
            onEditToggle={() => handleEditToggle('users')}
            onSave={() => handleSave('users')}
          />
        </TabsContent>
        
        <TabsContent value="notifications" className="mt-0">
          <NotificationPreferences 
            editing={editing.notifications}
            onEditToggle={() => handleEditToggle('notifications')}
            onSave={() => handleSave('notifications')}
          />
        </TabsContent>
        
        <TabsContent value="system" className="mt-0">
          <SystemConfiguration 
            editing={editing.system}
            onEditToggle={() => handleEditToggle('system')}
            onSave={() => handleSave('system')}
          />
        </TabsContent>
        
        <TabsContent value="security" className="mt-0">
          <SecurityAccessSettings 
            editing={editing.security}
            onEditToggle={() => handleEditToggle('security')}
            onSave={() => handleSave('security')}
          />
        </TabsContent>
        
        {/* Integrations */}
        <TabsContent value="ai" className="mt-0">
          <AIIntegrationSettings 
            editing={editing.ai}
            onEditToggle={() => handleEditToggle('ai')}
            onSave={() => handleSave('ai')}
          />
        </TabsContent>
        
        <TabsContent value="billing" className="mt-0">
          <TariffBillingSettings 
            editing={editing.billing}
            onEditToggle={() => handleEditToggle('billing')}
            onSave={() => handleSave('billing')}
          />
        </TabsContent>
        
        <TabsContent value="sustainability" className="mt-0">
          <SustainabilitySettings 
            editing={editing.sustainability}
            onEditToggle={() => handleEditToggle('sustainability')}
            onSave={() => handleSave('sustainability')}
          />
        </TabsContent>
        
        <TabsContent value="webhooks" className="mt-0">
          <WebhooksApiSettings 
            editing={editing.webhooks}
            onEditToggle={() => handleEditToggle('webhooks')}
            onSave={() => handleSave('webhooks')}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsContent;
